import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { AssistenteProvider } from "@features/ia/components/AssistenteProvider";
import { ClienteGuard } from "@/features/cliente/components/ClienteGuard";
import { ClienteRedirect } from "@/features/cliente/components/ClienteRedirect";
import ClienteDiario from "@/features/cliente/pages/ClienteDiario";
import ClienteHome from "@/features/cliente/pages/ClienteHome";

import DiarioEditar from "./features/diario/pages/DiarioEditar";
import DiarioNovo from "./features/diario/pages/DiarioNovo";
import ErrorBoundary from "./features/sistema/components/ErrorBoundary";
import Layout from "./features/sistema/components/Layout";
import { Loading } from "./features/sistema/components/Loading";
import { useAuth } from "./features/sistema/hooks/useAuth";
import { supabase } from "./features/sistema/services/supabaseClient";
import { GoogleCalendarAutoSyncProvider } from "./features/tarefas/components/GoogleCalendarAutoSyncProvider";

// ? PORTAL DO CLIENTE - Import direto
const Home = lazy(() => import('./features/sistema/pages/Home'));
const Login = lazy(() => import('./features/sistema/pages/Login'));

// ✅ PÁGINAS PRINCIPAIS - IMPORT DIRETO (alta prioridade, sem bugs de contexto)
import Dashboard from './features/sistema/pages/Dashboard';
const DashboardsCentral = lazy(() => import('./features/sistema/pages/DashboardsCentral'));

// ✅ PÁGINAS PRINCIPAIS - Lazy Loading
const Obras = lazy(() => import('./features/sistema/pages/Obras'));
const MinhasTarefas = lazy(() => import('@features/tarefas/pages/MinhasTarefas'));
const DiariosList = lazy(() => import('./features/diario/pages/DiariosList'));

// ✅ DIÁRIOS - IMPORT DIRETO (funcionalidade crítica, evita bugs)

// ✅ PÁGINAS AUXILIARES - Lazy Loading (reduz bundle inicial)
const ObraNova = lazy(() => import('./features/sistema/pages/ObraNova'));
const ObraDetalhes = lazy(() => import('./features/sistema/pages/ObraDetalhes'));
const ObraEditar = lazy(() => import('./features/sistema/pages/ObraEditar'));

const ChecklistList = lazy(() => import('./features/checklist/pages/ChecklistList'));
const ChecklistNovo = lazy(() => import('./features/checklist/pages/ChecklistNovo'));
const ChecklistEditar = lazy(() => import('./features/checklist/pages/ChecklistEditar'));
const ChecklistTemplatesEditor = lazy(() => import('./features/checklist/pages/ChecklistTemplatesEditor'));

const TarefasList = lazy(() => import('@features/tarefas/pages/TarefasList'));

// ✅ NOVO: Dashboard de Automações
const DashboardAutomacoes = lazy(() => import('@features/ia/pages/DashboardAutomacoes'));
// ✅ NOVO: Página do Assistente (lazy)
const AssistentePage = lazy(() => import('@/features/ia/pages/AssistentePage'));

const GerenciarUsuarios = lazy(() => import('./features/sistema/pages/GerenciarUsuarios'));
const NotificationsPage = lazy(() => import('@features/comunicacao/pages/NotificationsPage'));
const Configuracoes = lazy(() => import('@features/sistema/pages/Configuracoes'));
const FotosAnexos = lazy(() => import('@features/midia/pages/FotosAnexos'));
const Cronograma = lazy(() => import('@features/cronograma/pages/Cronograma'));

// ✅ COMPRAS - Lazy Loading
const ComprasHome = lazy(() => import('./features/compras/pages/ComprasHome'));
const RequisicoesList = lazy(() => import('./features/compras/pages/RequisicoesList'));
const ServicosPage = lazy(() => import('@features/servicos/pages/ServicosPage'));
const EmpresasPage = lazy(() => import('@/features/sistema/pages/EmpresasPage'));
const MateriaisCatalogoPage = lazy(() => import('@/features/sistema/pages/MateriaisCatalogoPage'));
const RequisicaoEditor  = lazy(() => import('@features/compras/pages/RequisicaoEditor'));
const PedidoWhatsApp    = lazy(() => import('@features/compras/pages/PedidoWhatsApp'));
const CotacaoComparativo = lazy(() => import('./features/compras/pages/CotacaoComparativo'));
// ✅ SERVIÇOS (GERAL) - Lazy Loading (visão multiobras)
const ServicosTodasObrasPage = lazy(() => import('@features/servicos/pages/ServicosTodasObrasPage'));
// ✅ PPC - Percentual de Pacotes Concluídos
const PPCPage = lazy(() => import('@features/servicos/pages/PPCPage'));
const PPCCadastrosPage = lazy(() => import('@features/servicos/pages/PPCCadastrosPage'));

// ✅ FINANCEIRO — MVP 1
const FinanceiroHome      = lazy(() => import('@/features/financeiro/pages/FinanceiroHome'));
const FinanceiroAP        = lazy(() => import('@/features/financeiro/pages/FinanceiroAP'));
const FinanceiroBancos    = lazy(() => import('@/features/financeiro/pages/FinanceiroBancos'));
const FinanceiroCadastros = lazy(() => import('@/features/financeiro/pages/FinanceiroCadastros'));
// ✅ FINANCEIRO — novas páginas
const FinanceiroAR        = lazy(() => import('@/features/financeiro/pages/FinanceiroAR'));
const FinanceiroRelatorios= lazy(() => import('@/features/financeiro/pages/FinanceiroRelatorios'));

// ✅ CONTRATOS - NOVO: página de Contratos por Obra
const ContratosObra = lazy(() => import('@/features/contratos/pages/ContratosObra'));

// ✅ ORÇAMENTOS - Lazy Loading
const OrcamentosHome = lazy(() => import('@/features/orcamentos/pages/OrcamentosHome'));
const OrcamentoEditor = lazy(() => import('@/features/orcamentos/pages/OrcamentoEditor'));
const DashboardOrcamentos = lazy(() => import('@/features/orcamentos/pages/DashboardOrcamentos'));
const CatalogoItensPage = lazy(() => import('@/features/orcamentos/pages/CatalogoItensPage'));

// ✅ COMPONENTE DE LOADING PARA PÚBLICAS
const PageSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <div className="min-h-screen bg-background dark:bg-dark-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loading />
          <p className="text-foreground/70 dark:text-dark-foreground/70 font-ageo-regular text-sm">
            Carregando página...
          </p>
        </div>
      </div>
    }
  >
    {children}
  </Suspense>
);

// ✅ COMPONENTE DIRETO PARA PÁGINAS PRINCIPAIS (sem Suspense)
const ProtectedPage = ({ children }: { children: React.ReactNode }) => (
  <Layout>
    {children}
  </Layout>
);

// ✅ COMPONENTE COM SUSPENSE PARA PÁGINAS AUXILIARES
const ProtectedPageLazy = ({ children }: { children: React.ReactNode }) => (
  <Layout>
    <Suspense
      fallback={
        <div className="min-h-96 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loading />
            <p className="text-foreground/70 dark:text-dark-foreground/70 font-ageo-regular text-sm">
              Carregando...
            </p>
          </div>
        </div>
      }
    >
      {children}
    </Suspense>
  </Layout>
);

function App() {
  const { user } = useAuth();

  return (
    <Router basename="/ERP-Acor">
      <AssistenteProvider>
        {/* 📅 Auto-sync Google Calendar (só ativa quando configurado) */}
        <GoogleCalendarAutoSyncProvider>
        {/* 🔄 Checar atualização do SW a cada navegação + prompt controlado */}
        <RouteChangeUpdater />
        <SWUpdatePrompt />
        {/* 🔐 Gate Único de Autenticação (primeiro paint) */}
        <AppInitGate>
          <ErrorBoundary>
            <Routes>
          {/* ✅ ROTAS PÚBLICAS - Lazy Loading */}
          <Route 
            path="/" 
            element={
              !user ? (
                <PageSuspense>
                  <Home />
                </PageSuspense>
              ) : (
                <RoleRedirect />
              )
            } 
          />
          <Route 
            path="/login" 
            element={
              <PageSuspense>
                <Login />
              </PageSuspense>
            } 
          />
          
          {/* ✅ ROTAS PROTEGIDAS */}
          {user ? (
            <>
              {/* 🚀 PÁGINAS PRINCIPAIS - IMPORT DIRETO (bundle principal) */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedPage>
                    <Dashboard />
                  </ProtectedPage>
                } 
              />
              {/* 🤖 ASSISTENTE - Página completa */}
              <Route
                path="/assistente"
                element={
                  <ProtectedPageLazy>
                    <AssistentePage />
                  </ProtectedPageLazy>
                }
              />
              
              <Route
                path="/dashboards"
                element={
                  <ProtectedPageLazy>
                    <DashboardsCentral />
                  </ProtectedPageLazy>
                }
              />
              
              <Route 
                path="/obras" 
                element={
                  <ProtectedPageLazy>
                    <Obras />
                  </ProtectedPageLazy>
                } 
              />

              <Route 
                path="/minhas-tarefas" 
                element={
                  <ProtectedPageLazy>
                    <MinhasTarefas />
                  </ProtectedPageLazy>
                } 
              />
              
              {/* 🚀 DIÁRIOS - Lazy Loading */}
              <Route 
                path="/obras/:id/diarios" 
                element={
                  <ProtectedPageLazy>
                    <DiariosList />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id/diarios/novo" 
                element={
                  <ProtectedPage>
                    <DiarioNovo />
                  </ProtectedPage>
                } 
              />
              <Route 
                path="/obras/:id/diarios/:relatorioId/editar" 
                element={
                  <ProtectedPage>
                    <DiarioEditar />
                  </ProtectedPage>
                } 
              />

              {/* 📦 OBRAS AUXILIARES - Lazy Loading */}
              <Route 
                path="/obras/nova" 
                element={
                  <ProtectedPageLazy>
                    <ObraNova />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id" 
                element={
                  <ProtectedPageLazy>
                    <ObraDetalhes />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id/editar" 
                element={
                  <ProtectedPageLazy>
                    <ObraEditar />
                  </ProtectedPageLazy>
                } 
              />
              
              {/* 📦 CHECKLIST - Lazy Loading */}
              <Route 
                path="/obras/:id/checklist" 
                element={
                  <ProtectedPageLazy>
                    <ChecklistList />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id/checklist/novo" 
                element={
                  <ProtectedPageLazy>
                    <ChecklistNovo />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id/checklist/:checklist_id" 
                element={
                  <ProtectedPageLazy>
                    <ChecklistEditar />
                  </ProtectedPageLazy>
                } 
              />
              
              {/* 📋 EDITOR DE TEMPLATES DE CHECKLIST */}
              <Route 
                path="/checklists/templates" 
                element={
                  <ProtectedPageLazy>
                    <ChecklistTemplatesEditor />
                  </ProtectedPageLazy>
                } 
              />

              {/* 📦 TAREFAS - Lazy Loading */}
              <Route 
                path="/obras/:id/tarefas" 
                element={
                  <ProtectedPageLazy>
                    <TarefasList />
                  </ProtectedPageLazy>
                } 
              />

              {/* 🤖 DASHBOARD DE AUTOMAÇÕES - Lazy Loading */}
              <Route 
                path="/obras/:id/dashboard-automacoes" 
                element={
                  <ProtectedPageLazy>
                    <DashboardAutomacoes />
                  </ProtectedPageLazy>
                } 
              />
              
              {/* 📦 COMPRAS - Rotas globais */}
              <Route 
                path="/compras" 
                element={
                  <ProtectedPageLazy>
                    <ComprasHome />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/compras/requisicoes/nova" 
                element={
                  <ProtectedPageLazy>
                    <RequisicaoEditor />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/compras/requisicoes/:id" 
                element={
                  <ProtectedPageLazy>
                    <RequisicaoEditor />
                  </ProtectedPageLazy>
                } 
              />
              <Route path="/compras/requisicoes/:id/pedido" element={
                <ProtectedPageLazy>
                  <PedidoWhatsApp />
                </ProtectedPageLazy>
              } />
                             <Route 
                 path="/compras/cotacao/:id" 
                 element={
                   <ProtectedPageLazy>
                     <CotacaoComparativo />
                   </ProtectedPageLazy>
                 } 
               />
              <Route 
                path="/compras/pedido/:id" 
                element={
                  <ProtectedPageLazy>
                    <PedidoWhatsApp />
                  </ProtectedPageLazy>
                } 
              />

              {/* 📦 COMPRAS - Por Obra */}
              <Route 
                path="/obras/:id/compras" 
                element={
                  <ProtectedPageLazy>
                    <RequisicoesList />
                  </ProtectedPageLazy>
                } 
              />
              {/* rota de criação removida — criação agora é via modal ou Editor */}

              {/* 📦 PÁGINAS AUXILIARES - Lazy Loading */}
              <Route 
                path="/obras/:id/fotos" 
                element={
                  <ProtectedPageLazy>
                    <FotosAnexos />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/obras/:id/cronograma" 
                element={
                  <ProtectedPageLazy>
                    <Cronograma />
                  </ProtectedPageLazy>
                } 
              />

              {/* 📋 CONTRATOS - Lista de contratos da obra + medições (single page) */}
              <Route 
                path="/obras/:id/contratos" 
                element={
                  <ProtectedPageLazy>
                    <ContratosObra />
                  </ProtectedPageLazy>
                } 
              />

              {/* 📦 SERVIÇOS - novo módulo, complemento do Cronograma */}
              <Route 
                path="/obras/:id/servicos" 
                element={
                  <ProtectedPageLazy>
                    <ServicosPage />
                  </ProtectedPageLazy>
                } 
              />

              {/* 📊 PPC - Percentual de Pacotes Concluídos */}
              <Route
                path="/obras/:id/ppc"
                element={
                  <ProtectedPageLazy>
                    <PPCPage />
                  </ProtectedPageLazy>
                }
              />

              {/* 🧰 SERVIÇOS (GERAL) - visão consolidada multiobras */}
              <Route
                path="/servicos/geral"
                element={
                  <ProtectedPageLazy>
                    <ServicosTodasObrasPage />
                  </ProtectedPageLazy>
                }
              />

              {/* 📇 EMPRESAS - cadastro mestre */}
              <Route
                path="/empresas"
                element={
                  <ProtectedPageLazy>
                    <EmpresasPage />
                  </ProtectedPageLazy>
                }
              />

              <Route 
                path="/usuarios" 
                element={
                  <ProtectedPageLazy>
                    <GerenciarUsuarios />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/usuarios/novo" 
                element={
                  <ProtectedPageLazy>
                    {/* Abre a mesma página com modal Novo Usuário */}
                    <GerenciarUsuarios />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/notificacoes" 
                element={
                  <ProtectedPageLazy>
                    <NotificationsPage />
                  </ProtectedPageLazy>
                } 
              />
              <Route 
                path="/configuracoes" 
                element={
                  <ProtectedPageLazy>
                    <Configuracoes />
                  </ProtectedPageLazy>
                } 
              />
              {/* ⚙️ Configurações → Materiais Catálogo */}
              <Route
                path="/configuracoes/materiais-catalogo"
                element={
                  <ProtectedPageLazy>
                    <MateriaisCatalogoPage />
                  </ProtectedPageLazy>
                }
              />

              {/* 💰 FINANCEIRO — MVP 1 */}
              <Route
                path="/financeiro"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroHome />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/financeiro/ap"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroAP />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/financeiro/bancos"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroBancos />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/financeiro/cadastros"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroCadastros />
                  </ProtectedPageLazy>
                }
              />
              {/* 💰 FINANCEIRO — novas rotas */}
              <Route
                path="/financeiro/ar"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroAR />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/financeiro/relatorios"
                element={
                  <ProtectedPageLazy>
                    <FinanceiroRelatorios />
                  </ProtectedPageLazy>
                }
              />

              {/* 📊 ORÇAMENTOS */}
              <Route
                path="/orcamentos"
                element={
                  <ProtectedPageLazy>
                    <OrcamentosHome />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/orcamentos/:id"
                element={
                  <ProtectedPageLazy>
                    <OrcamentoEditor />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/orcamentos-dashboard"
                element={
                  <ProtectedPageLazy>
                    <DashboardOrcamentos />
                  </ProtectedPageLazy>
                }
              />
              {/* 📊 PPC Cadastros - Categorias de não execução */}
              <Route
                path="/cadastros/ppc-categorias"
                element={
                  <ProtectedPageLazy>
                    <PPCCadastrosPage />
                  </ProtectedPageLazy>
                }
              />
              <Route
                path="/cadastros/catalogo-itens"
                element={
                  <ProtectedPageLazy>
                    <CatalogoItensPage />
                  </ProtectedPageLazy>
                }
              />

              {/* 🏠 PORTAL DO CLIENTE */}
              <Route path="/cliente" element={<ClienteRedirect />} />
              <Route element={<ClienteGuard />}>
                <Route path="/cliente/obras/:obraId" element={<ClienteHome />} />
                <Route path="/cliente/obras/:obraId/diarios/:relatorioId" element={<ClienteDiario />} />
              </Route>
            </>
          ) : (
            // Redireciona para login se não estiver autenticado
            <Route path="*" element={<Navigate to="/" replace />} />
          )}

          {/* 🚫 FALLBACK - Usuários sem acesso */}
          <Route path="/sem-acesso" element={<div className="p-4">Sem acesso a nenhuma obra. Contate a ACOR.</div>} />
            </Routes>
          </ErrorBoundary>
        </AppInitGate>
        </GoogleCalendarAutoSyncProvider>
      </AssistenteProvider>
    </Router>
  );
}

export default App;

// =========================
// 🔐 Gate Único de Autenticação (inline)
// Mostra um único loader global enquanto useAuth() resolve.
function AppInitGate({ children }: { children: React.ReactNode }) {
  const { loading, user } = useAuth();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 🔄 Processa tokens vindos do RH (ou outros sistemas externos)
  useEffect(() => {
    const processExternalTokens = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        if (accessToken && refreshToken) {
          console.log('🔄 AppInitGate: Tokens recebidos de sistema externo, restaurando sessão...');

          // Restaura a sessão do Supabase
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('❌ AppInitGate: Erro ao restaurar sessão:', error);
          } else {
            console.log('✅ AppInitGate: Sessão restaurada com sucesso');
          }

          // Limpa os tokens da URL (mantém path limpo)
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        }
      } catch (error) {
        console.error('❌ AppInitGate: Erro ao processar tokens externos:', error);
      }
    };

    processExternalTokens();
  }, []); // Executa apenas uma vez no mount

  // Timeout de segurança para evitar travamento infinito
  useEffect(() => {
    // Limpar timeout anterior se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Só criar timeout se ainda estiver carregando
    if (loading) {
      timeoutRef.current = setTimeout(() => {
        console.warn('⚠️ AppInitGate: Timeout de 10s atingido, forçando renderização')
        setTimeoutReached(true);
      }, 10000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loading]);

  // Reset timeout quando user muda
  useEffect(() => {
    if (user !== null) {
      setTimeoutReached(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [user]);

  if (loading && !timeoutReached) {
    return (
      <div className="min-h-screen bg-background dark:bg-dark-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loading />
          <p className="text-foreground dark:text-dark-foreground font-ageo-regular">
            Inicializando ACOR ERP...
          </p>
        </div>
      </div>
    );
  }

  if (timeoutReached) {
    console.log('⚠️ AppInitGate: Renderizando com timeout - pode haver problemas de conectividade')
  }

  return <>{children}</>;
}

// =========================
// 🔄 Checagem de atualização do SW a cada mudança de rota (throttle 30s)
declare global {
  interface Window {
    __lastSWCheck?: number;
    __pwaCheckForUpdate?: (reason?: string) => Promise<void>;
    __pwaApplyUpdate?: () => void;
    __pwaHasWaiting?: boolean;
  }
}
function RouteChangeUpdater() {
  const location = useLocation();
  useEffect(() => {
    const now = Date.now();
    const last = window.__lastSWCheck ?? 0;
    if (now - last < 30_000) return; // throttle 30s
    window.__lastSWCheck = now;
    if (navigator.onLine && !window.__pwaHasWaiting) {
      window.__pwaCheckForUpdate?.('route-change');
    }
  }, [location.pathname]);
  // 🔹 NOVO: também notifica "quem quiser ouvir" que a rota mudou
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('app:refresh', { detail: { scope: location.pathname } }));
  }, [location.pathname]);
  return null;
}

// =========================
// 📣 Prompt para aplicar atualização (skipWaiting)
function SWUpdatePrompt() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onReady = () => setOpen(true);
    window.addEventListener('acor:sw-update-ready', onReady);
    return () => window.removeEventListener('acor:sw-update-ready', onReady);
  }, []);
  if (!open) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[1000] rounded-xl bg-foreground text-background dark:bg-dark-foreground dark:text-dark-background px-4 py-2 shadow-lg flex items-center gap-3">
      <span className="text-sm">Nova versão disponível.</span>
      <button className="underline text-sm" onClick={() => window.__pwaApplyUpdate?.()}>
        Atualizar agora
      </button>
    </div>
  );
}

// 🔀 Decide destino por role (com verificação de email cadastrado)
function RoleRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        console.log('🔄 RoleRedirect: Iniciando verificação de role...')

        // 1) Garante sessão válida; se refresh token falhar, limpa e volta pro login
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError && /refresh token/i.test(sessionError.message)) {
          console.warn('❌ RoleRedirect: Sessão inválida, limpando e redirecionando…', sessionError);
          await supabase.auth.signOut({ scope: 'local' });
          navigate('/login', { replace: true });
          return;
        }
        const uid = sessionData?.session?.user?.id;
        const userEmail = sessionData?.session?.user?.email;
        if (!uid || !userEmail) {
          console.log('❌ RoleRedirect: Usuário não encontrado, redirecionando para login')
          navigate('/login', { replace: true });
          return;
        }

        console.log('🔄 RoleRedirect: Buscando usuário pelo email:', userEmail)

        // Buscar usuário pelo email (não pelo ID, pois pode ser o primeiro login)
        const { data: usuario, error: userError } = await supabase
          .from('usuarios')
          .select('id, role, status')
          .eq('email', userEmail)
          .maybeSingle();

        if (userError) {
          console.error('❌ RoleRedirect: Erro ao buscar usuário:', userError)
          navigate('/login', { replace: true });
          return;
        }

        // Verificar se o email está cadastrado no sistema
        if (!usuario) {
          console.log('❌ RoleRedirect: Email não cadastrado no sistema')
          await supabase.auth.signOut({ scope: 'local' });
          navigate('/?erro=nao_cadastrado', { replace: true });
          return;
        }

        // Verificar se o usuário está ativo
        if (usuario.status !== 'ativo') {
          console.log('❌ RoleRedirect: Usuário inativo')
          await supabase.auth.signOut({ scope: 'local' });
          navigate('/?erro=inativo', { replace: true });
          return;
        }

        // Se o ID na tabela usuarios for diferente do ID do Auth, atualizar
        if (usuario.id !== uid) {
          console.log('🔄 RoleRedirect: Atualizando ID do usuário para vincular ao Auth')
          const { error: updateError } = await supabase
            .from('usuarios')
            .update({ id: uid })
            .eq('email', userEmail);

          if (updateError) {
            console.error('❌ RoleRedirect: Erro ao atualizar ID:', updateError)
            // Continua mesmo com erro, pois o usuário existe
          }
        }

        console.log('🔄 RoleRedirect: Role encontrado:', usuario.role)

        if (usuario.role === 'cliente') {
          console.log('🔄 RoleRedirect: Usuário é cliente, buscando obra...')
          const { data: vinc, error: vincError } = await supabase
            .from('obras_clientes')
            .select('obra_id')
            .eq('user_id', uid)
            .eq('ativo', true)
            .maybeSingle();

          if (vincError) {
            console.error('❌ RoleRedirect: Erro ao buscar obra do cliente:', vincError)
            navigate('/sem-acesso', { replace: true });
            return;
          }

          if (vinc?.obra_id) {
            console.log('✅ RoleRedirect: Redirecionando cliente para obra:', vinc.obra_id)
            navigate(`/cliente/obras/${vinc.obra_id}`, { replace: true });
          } else {
            console.log('❌ RoleRedirect: Cliente sem obra liberada')
            navigate('/sem-acesso', { replace: true });
          }
        } else {
          console.log('✅ RoleRedirect: Redirecionando para /obras')
          navigate('/obras', { replace: true });
        }
      } catch (error) {
        console.error('❌ RoleRedirect: Erro inesperado:', error)
        navigate('/login', { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background dark:bg-dark-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loading />
        <p className="text-foreground/70 dark:text-dark-foreground/70 font-ageo-regular text-sm">
          Direcionando...
        </p>
      </div>
    </div>
  );
}

